import React, { FC, useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import CryptoJS from 'crypto-js';
import { useQuery, useInterval, useLocalization, useMobileMasterpassVersion } from 'Hooks';
import { onGetMasterpassForMobileRequest } from 'Stores/CreditCardTransactions';
import { MasterPassTokenEnum } from 'Utils';
import { routePath } from 'Navigator/routes';
import './MobilePayment.scss';
import { masterPassPaymentNormalize } from 'Normalize';

interface MobilePaymentProps {}

interface ActiveSelectedCreditCard {
  /**
   * ⚠️ Saga kayıtsız kart ödemesine BU ALANA bakarak karar veriyor
   * (`activeSelectedCard?.cardNumber`) — web akışıyla ortak kod. Yalnızca
   * `rtaPan` verildiğinde kayıtsız kart ödemesi yanlış dala düşüyordu.
   */
  cardNumber?: string;
  rtaPan: string;
  expiryDate: string;
  cvv: string;
}

const MobilePayment: FC<MobilePaymentProps> = () => {
  const [remainingSeconds, setRemainingSeconds] = useState<number>(180);
  const dispatch = useDispatch();
  const history = useHistory();
  const url = useQuery();
  const { masterpass_hash_key, masterpass_hash_id } = useLocalization();

  useInterval(() => {
    if (remainingSeconds > 0) {
      setRemainingSeconds(remainingSeconds - 1);
    }
  }, 1000);

  const decryptData = useCallback(
    (encryptedData: string) => {
      // ⚠️ İKİ KODLAMA DA DESTEKLENİR — MOBİL SÜRÜM AYRIMI YAPILMAZ.
      //
      // Eski uygulama sürümleri `data` alanını ÇİFT base64'lüyor: AES çıktısı
      // (zaten base64) bir kez daha base64'leniyor. Bu %33 şişme, kayıtsız kart
      // ödemesinde adresi test ortamındaki IIS/WAF'ın 2048 baytlık sınırının
      // üstüne çıkarıp sayfa açılmadan 404'e düşürüyordu. Yeni sürümler tek
      // katman gönderiyor (base64url).
      //
      // Ayrım şifreli metnin kendisinden yapılır: CryptoJS'in OpenSSL biçimi
      // daima "Salted__" ile başlar, base64'te "U2FsdGVkX1". Çift kodlamada ise
      // bunun base64'ü, yani "VTJGc2RHVmtY" görünür.
      const normalized = encryptedData.replace(/-/g, '+').replace(/_/g, '/');
      const decData = normalized.startsWith('U2FsdGVkX1')
        ? normalized
        : CryptoJS.enc.Base64.parse(normalized).toString(CryptoJS.enc.Utf8);

      return CryptoJS.AES.decrypt(decData, masterpass_hash_key || 'default_key', {
        iv: CryptoJS.enc.Hex.parse(masterpass_hash_id || 'default_iv'),
      }).toString(CryptoJS.enc.Utf8);
    },
    [masterpass_hash_key, masterpass_hash_id]
  );

  // ⚠️ Sürüm bayrağı burada ŞİFRELİ GÖVDEDEN geliyor (adres çubuğundan değil),
  // çünkü bu ekranın parametreleri AES ile şifrelenmiş `data` alanında taşınıyor.
  const [mobileMasterpassVersionFlag, setMobileMasterpassVersionFlag] = useState<string | boolean | null>(null);
  useMobileMasterpassVersion(mobileMasterpassVersionFlag);

  const processPayment = useCallback((json: any) => {
    setMobileMasterpassVersionFlag(json.isVersion2Active ?? null);

    const activeSelectedCreditCard: ActiveSelectedCreditCard | null =
      json.rtaPan && json.expiryDate && json.cvc
        ? {
            // ⚠️ `cardNumber` ALANI ŞART. Saga kayıtsız kart ödemesine
            // `activeSelectedCard?.cardNumber` kontrolüyle karar veriyor
            // (web akışıyla aynı kod). Burada yalnızca `rtaPan` verildiğinde o
            // kontrol ASLA sağlanmıyor ve kayıtsız kart ödemesi, kayıtlı kart
            // dalına (`payment`) düşüp boş `cardAlias` ile gidiyordu.
            cardNumber: json.rtaPan,
            rtaPan: json.rtaPan,
            expiryDate: json.expiryDate,
            cvv: json.cvc,
          }
        : null;

    dispatch(
      onGetMasterpassForMobileRequest({
        activeSelectedCreditCard,
        TokenType: MasterPassTokenEnum.REGISTER_PURCHASE,
        // ⚠️ SÜRÜM BAYRAĞI PAYLOAD'A DOĞRUDAN KONUR — STORE'DAN OKUNMASI BEKLENMEZ.
        //
        // `useMobileMasterpassVersion` bayrağı store'a bir `useEffect` içinde
        // yazıyor; efekt ancak BİR SONRAKİ render'da çalışıyor. Bu dispatch ise
        // AYNI senkron turda yapılıyor ve saga dispatch anında senkron başlıyor.
        // Yani saga store'u okuduğunda `masterpassVersion` HÂLÂ `undefined` ve
        // varsayılan olan v1'e düşüyordu: v2 JWT'si v1 MFS servisine gidiyor,
        // servis de 4 haneli fault kodu (`1419`) ile reddediyordu.
        //
        // Bu yarış yalnızca BU ekranda var; diğer mobil ekranlar bayrağı adres
        // çubuğundan okuyor ve değer ilk render'da hazır oluyor.
        isVersion2Active: json.isVersion2Active,
        // ⚠️ v2 `userId` olarak müşteri numarası bekliyor; şifreli gövdede geliyor.
        CustomerNumber: json.customerNumber,
        referenceNo: json.referenceNo,
        PaymentNumber: json.paymentNumber,
        // ⚠️ TERMİNAL GRUBU BELBİM'İN CEVABINDAN GELİR, SABİTTEN DEĞİL.
        //
        // v1'de ödeme isteğine token cevabındaki `MacroMerchantId` yazılıyordu ve
        // o alan terminal grubunu taşıyordu. v2'de anlamı değişti: terminal grubu
        // artık `MpTerminalGroupId`. Panda bu alanı bir dönem hiç iletmedi, saga da
        // ortam sabitine düştü — TL yüklemede iki değer aynı olduğu için fark
        // edilmedi, ancak kart başvurusu KENDİ POS'unda koştuğu için işlem yanlış
        // terminale gidip `5177 — Sadece Sandbox kredi kartları ile ödeme
        // yapılabilir` ile reddedildi.
        //
        // ⚠️ ESKİ SÜRÜMLER İÇİN `macroMerchantId` DE İLETİLMELİ. Bu alan yalnızca
        // `MpTerminalGroupId` okunacak şekilde bırakıldığında, alanı göndermeyen
        // saha sürümleri saga'da ortam sabitine düşüyor ve kart başvurusu ödemesi
        // TL yüklemenin genel terminaline gidip Masterpass'ten
        // `Terminal group ID in the request is different from the terminal group
        //  ID provided in the orginal transaction` alıyordu. v1'de iki alan aynı
        // değeri taşıdığı için `macroMerchantId` güvenli bir yedektir.
        MpTerminalGroupId: json.mpTerminalGroupId,
        // v1'de terminal grubunu taşır; saga yalnızca v1'de yedek olarak okur.
        macroMerchantId: json.macroMerchantIdParam,
        token: json.token,
        returnUrl: json.returnUrl,
        request: {
          data: masterPassPaymentNormalize(
            Number(json.loadAmount) + Number(json.commissionAmount),
            json.creditCardName,
            json?.macroMerchantIdParam
          ),
          phoneNumberForMobile: json.phone,
        },
      })
    );
  }, []);
  useEffect(() => {
    const fetchData = () => {
      const data = url.get('data');
      if (!data) {
        history.push(routePath.welcome);
        return;
      }

      try {
        const decryptedData = decryptData(data);
        const json = JSON.parse(decryptedData);
        processPayment(json);
      } catch (error) {
        console.error('Error processing payment data:', error);
        // Handle error appropriately
      }
    };

    fetchData();
  }, [decryptData, processPayment]);

  return <div id="mobilePayment"></div>;
};

export default MobilePayment;
